import { useState, useRef, useEffect, useCallback } from 'react'
import { Check, ChevronDown, X } from 'lucide-react'
import './StatusFilter.css'

interface MultiSelectFilterProps {
  /** Selected option values; an empty array means "all" (no filter). */
  value: string[]
  onChange: (value: string[]) => void
  options: Array<{ value: string; label: string }>
  label: string
  /** Analytics event name prefix (e.g. `bug-kind-filter`) applied to the trigger/clear/options. */
  analyticsPrefix: string
  /** data-testid prefix so multiple filters on one toolbar stay distinct. */
  testId: string
}

// The checkbox flavour of StatusFilter: same trigger/clear/dropdown chrome, but options toggle in and out
// of the selection and the dropdown stays open so several can be picked in one go. A leading "All" row
// clears the selection.
export default function MultiSelectFilter({ value, onChange, options, label, analyticsPrefix, testId }: MultiSelectFilterProps) {
  const [open, setOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const ref = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([])
  // Index 0 is the "All" row; option i sits at i + 1.
  const rowCount = options.length + 1
  const active = value.length > 0

  useEffect(() => {
    if (!open) return
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  useEffect(() => {
    if (open) setFocusedIndex(0)
  }, [open])

  useEffect(() => {
    if (open && focusedIndex >= 0) optionRefs.current[focusedIndex]?.focus()
  }, [open, focusedIndex])

  const closeAndFocusTrigger = useCallback(() => {
    setOpen(false)
    triggerRef.current?.focus()
  }, [])

  function toggle(optionValue: string) {
    onChange(value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      // Keep the selection in option order so the request (and the trigger label) is stable.
      : options.map(o => o.value).filter(v => v === optionValue || value.includes(v)))
  }

  function handleTriggerKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (!open) setOpen(true)
    } else if (e.key === 'Escape' && open) {
      e.preventDefault()
      closeAndFocusTrigger()
    }
  }

  function handleOptionKeyDown(e: React.KeyboardEvent, index: number) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setFocusedIndex(Math.min(index + 1, rowCount - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        if (index === 0) closeAndFocusTrigger()
        else setFocusedIndex(index - 1)
        break
      case 'Home':
        e.preventDefault()
        setFocusedIndex(0)
        break
      case 'End':
        e.preventDefault()
        setFocusedIndex(rowCount - 1)
        break
      case 'Escape':
        e.preventDefault()
        closeAndFocusTrigger()
        break
    }
  }

  const firstLabel = options.find(o => o.value === value[0])?.label ?? value[0]
  const triggerLabel = !active ? label : value.length === 1 ? firstLabel : `${firstLabel} +${value.length - 1}`
  const listboxId = `${label.toLowerCase().replace(/\s+/g, '-')}-filter-listbox`

  return (
    <div className="status-filter" ref={ref} data-testid={testId}>
      <div className="status-filter__controls">
        <button
          type="button"
          ref={triggerRef}
          className={`status-filter__trigger ${active ? 'status-filter__trigger--active' : ''}`}
          onClick={() => setOpen(o => !o)}
          onKeyDown={handleTriggerKeyDown}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={open ? listboxId : undefined}
          title={active ? value.map(v => options.find(o => o.value === v)?.label ?? v).join(', ') : undefined}
          data-testid={`${testId}-trigger`}
          data-analytics-event={`${analyticsPrefix}-toggle`}
        >
          <span>{triggerLabel}</span>
          {!active && <ChevronDown size={14} aria-hidden="true" data-testid={`${testId}-chevron`} />}
        </button>
        {active && (
          <button
            type="button"
            className="status-filter__clear"
            onClick={() => onChange([])}
            aria-label={`Clear ${label.toLowerCase()} filter`}
            data-testid={`${testId}-clear`}
            data-analytics-event={`${analyticsPrefix}-clear`}
          >
            <X size={14} aria-hidden="true" />
          </button>
        )}
      </div>
      {open && (
        <div
          className="status-filter__dropdown"
          role="listbox"
          aria-multiselectable="true"
          id={listboxId}
          aria-label={`${label} filter options`}
          data-testid={`${testId}-dropdown`}
        >
          <button
            type="button"
            ref={el => { optionRefs.current[0] = el }}
            className={`status-filter__option status-filter__option--check ${!active ? 'status-filter__option--selected' : ''}`}
            onClick={() => onChange([])}
            onKeyDown={(e) => handleOptionKeyDown(e, 0)}
            role="option"
            aria-selected={!active}
            data-testid={`${testId}-option-all`}
            data-analytics-event={`${analyticsPrefix}-all`}
          >
            <span className="status-filter__checkbox" aria-hidden="true">{!active && <Check size={12} />}</span>
            All
          </button>
          {options.map((opt, i) => {
            const selected = value.includes(opt.value)
            return (
              <button
                type="button"
                key={opt.value}
                ref={el => { optionRefs.current[i + 1] = el }}
                className={`status-filter__option status-filter__option--check ${selected ? 'status-filter__option--selected' : ''}`}
                onClick={() => toggle(opt.value)}
                onKeyDown={(e) => handleOptionKeyDown(e, i + 1)}
                role="option"
                aria-selected={selected}
                data-testid={`${testId}-option-${opt.value}`}
                data-analytics-event={`${analyticsPrefix}-${opt.value}`}
              >
                <span className="status-filter__checkbox" aria-hidden="true">{selected && <Check size={12} />}</span>
                {opt.label}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
